import { useEffect, useState } from 'react';

/**
 * The open detail panel, shared with the floating chat.
 *
 * So that the chat can mark the record a page's detail panel shows (MI-343),
 * the panel says how wide it is while it is open, and its page says when the
 * address names another record.
 *
 * The width is measured, not computed: a panel opens at a share of its page
 * until the reader drags it, and CSS clamps it either way. The record is read
 * from the address, which is where every page already keeps the one it has
 * open. A page writes it there with `replaceState`, which tells nobody, so it
 * says so here.
 */
const PANEL_EVENT = 'mikapital:detailpanel';
let openWidth = 0;

const publish = (width) => {
  if (width === openWidth) return;
  openWidth = width;
  window.dispatchEvent(new Event(PANEL_EVENT));
};

/** A page wrote the record it has open to the address, or took it away. */
export const detailPanelMoved = () => window.dispatchEvent(new Event(PANEL_EVENT));

/**
 * Publish the width of the detail panel `handleRef` sits in, while `isOpen`.
 * `handleRef` is the panel's drag handle, whose parent is the panel itself.
 */
export function usePublishDetailPanel(handleRef, isOpen = true) {
  useEffect(() => {
    const panel = handleRef.current?.parentElement;
    if (!isOpen || !panel) return undefined;
    const measure = () => publish(Math.round(panel.getBoundingClientRect().width));
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(panel);
    return () => {
      observer?.disconnect();
      publish(0);
    };
  }, [handleRef, isOpen]);
}

const read = () => ({
  width: openWidth,
  address: `${window.location.pathname}${window.location.search}`,
});

/**
 * `{ width, address }`: the open detail panel's width in px (0 when no panel is
 * open), and the page's address as of the last time a panel opened, closed or
 * changed record.
 */
export function useDetailPanel() {
  const [panel, setPanel] = useState(read);
  useEffect(() => {
    const update = () => setPanel((prev) => {
      const next = read();
      return prev.width === next.width && prev.address === next.address ? prev : next;
    });
    update();
    window.addEventListener(PANEL_EVENT, update);
    return () => window.removeEventListener(PANEL_EVENT, update);
  }, []);
  return panel;
}
